import { useState, type ComponentType, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, type Role } from '../lib/auth';
import { useT } from '../lib/i18n';
import { InstallPrompt } from './InstallPrompt';
import { UpdateBanner } from './UpdateBanner';
import { ThemeSwitcher } from './ThemeSwitcher';
import { LanguageSwitcher } from './LanguageSwitcher';
import { InterfaceLanguage } from './InterfaceLanguage';
import { clearOfflineCaches } from '../lib/pwa';
import { PasswordInput } from './ui';
import {
  Album, BadgeCheck, BookOpen, CreditCard, Dices, Globe, Home, Images, LifeBuoy, Map, Menu as MenuIcon,
  Mic, Phone, Scroll, ShieldCheck, Sprout, SquarePen, TreeDeciduous, Users, History, Plane, GraduationCap, Compass,
} from 'lucide-react';

interface NavItem {
  to: string;
  /** A catalogue key, resolved at render so the sidebar follows the interface language. */
  label: string;
  /**
   * A drawn icon, not an emoji.
   *
   * The navigation used emoji, which look chosen from a keyboard rather than
   * designed: each platform draws them differently, they will not take the
   * interface's colour, and their weights do not match each other or the type.
   * These inherit currentColor and the stroke weight, so the sidebar reads as one
   * thing. Emoji stay where they are content — a 🐕 beside ajá in a child's
   * lesson is warm and correct.
   */
  icon: ComponentType<{ size?: number | string; strokeWidth?: number; 'aria-hidden'?: boolean }>;
  roles: Role[];
  group: string;
}

const ADULT_FAMILY: Role[] = ['parent', 'grandparent'];
// Five pillars: Home, Learn, Preserve, Family, Explore. Everything a family does
// sits under one of them; account and staff tools stay out of the way below.
const NAV: NavItem[] = [
  { to: '/', label: 'nav.familyHome', icon: Home, roles: ['parent', 'grandparent', 'teen'], group: 'nav.group.home' },
  { to: '/play', label: 'nav.playLearn', icon: Dices, roles: ['parent', 'child', 'teen'], group: 'nav.group.learn' },
  { to: '/roots', label: 'nav.roots', icon: Sprout, roles: ['parent', 'teen'], group: 'nav.group.learn' },
  { to: '/live', label: 'nav.liveCircles', icon: Users, roles: ['parent', 'teen'], group: 'nav.group.learn' },
  { to: '/preserve', label: 'nav.preserveMemory', icon: Mic, roles: [...ADULT_FAMILY, 'teen'], group: 'nav.group.preserve' },
  { to: '/stories', label: 'nav.storyVault', icon: Scroll, roles: ['parent', 'grandparent', 'teen', 'child'], group: 'nav.group.preserve' },
  { to: '/archive', label: 'nav.familyArchive', icon: Images, roles: ['parent', 'grandparent', 'teen', 'child'], group: 'nav.group.preserve' },
  { to: '/elder', label: 'nav.elderMode', icon: Album, roles: ADULT_FAMILY, group: 'nav.group.preserve' },
  { to: '/tree', label: 'nav.voiceTree', icon: TreeDeciduous, roles: ['parent', 'grandparent', 'teen', 'child'], group: 'nav.group.family' },
  { to: '/timeline', label: 'nav.timeline', icon: History, roles: ['parent', 'grandparent', 'teen'], group: 'nav.group.family' },
  { to: '/calls', label: 'nav.calls', icon: Phone, roles: [...ADULT_FAMILY, 'teen'], group: 'nav.group.family' },
  { to: '/legacy-book', label: 'nav.legacyBook', icon: BookOpen, roles: ['parent'], group: 'nav.group.family' },
  { to: '/trips', label: 'nav.trips', icon: Plane, roles: ['parent'], group: 'nav.group.explore' },
  { to: '/languages', label: 'nav.livingLanguages', icon: Map, roles: ['parent', 'grandparent', 'teen'], group: 'nav.group.explore' },
  { to: '/safety', label: 'nav.safety', icon: ShieldCheck, roles: ['parent'], group: 'nav.group.account' },
  { to: '/consent', label: 'nav.consent', icon: BadgeCheck, roles: ['parent'], group: 'nav.group.account' },
  { to: '/billing', label: 'nav.billing', icon: CreditCard, roles: ['parent'], group: 'nav.group.account' },
  { to: '/report', label: 'nav.report', icon: LifeBuoy, roles: ['parent', 'grandparent', 'teen', 'child', 'tutor'], group: 'nav.group.account' },
  { to: '/tutor', label: 'nav.tutorConsole', icon: GraduationCap, roles: ['tutor'], group: 'nav.group.work' },
  // One screen, two jobs. The page header already adapts; the navigation did not,
  // so a content editor was told they were a safeguarding admin every time they
  // looked at the sidebar. On a product where those roles are deliberately held
  // apart, the label a person sees should match the powers they actually have.
  { to: '/admin', label: 'nav.safeguardingAdmin', icon: Compass, roles: ['safeguarding_admin'], group: 'nav.group.work' },
  { to: '/admin', label: 'nav.contentReview', icon: SquarePen, roles: ['content_editor'], group: 'nav.group.work' },
];

export function Layout({ children }: { children?: ReactNode }) {
  const { user, childMode, signOut, exitChildMode } = useAuth();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [exitErr, setExitErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useT();
  if (!user) return null;
  const items = NAV.filter((n) => n.roles.includes(user.role));
  const groups = [...new Set(items.map((i) => i.group))];

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t('shell.skipToContent')}
      </a>
      <aside className={`sidebar ${open ? 'open' : ''}`} aria-label={t('shell.mainNavigation')}>
        <NavLink to="/" className="brand" onClick={() => setOpen(false)}>
          <img className="brand-mark" src="/favicon.svg" alt="" />
          <span>
            <span className="brand-name" lang="yo">
              Ìran
            </span>
            <span className="brand-sub">{t('shell.by')}</span>
          </span>
        </NavLink>
        <nav className="nav">
          {groups.map((g) => (
            <div key={g}>
              <div className="nav-group">{t(g)}</div>
              {items
                .filter((i) => i.group === g)
                .map((i) => (
                  <NavLink key={`${i.to}:${i.label}`} to={i.to} end={i.to === '/'} onClick={() => setOpen(false)}>
                    <span className="nav-icon" aria-hidden="true">
                      <i.icon size={18} strokeWidth={1.75} />
                    </span>
                    {t(i.label)}
                  </NavLink>
                ))}
            </div>
          ))}
        </nav>
      </aside>
      <div>
        <main className="main" id="main" tabIndex={-1}>
          <div className="topbar">
            <button className="btn ghost small mobile-nav-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
              <MenuIcon size={16} strokeWidth={2} aria-hidden={true} /> {t('shell.menu')}
            </button>
            <div />
            <div className="who">
              <LanguageSwitcher />
              <InterfaceLanguage />
              <ThemeSwitcher />
              <span>
                {user.displayName ?? t('shell.signedIn')} · <span className="badge">{user.role.replace('_', ' ')}</span>
              </span>
              {!childMode && (
                <button
                  className="btn ghost small"
                  onClick={() => {
                    signOut();
                    void clearOfflineCaches();
                    navigate('/signin');
                  }}
                >
                  Sign out
                </button>
              )}
            </div>
          </div>
          {childMode && (
            <form
              className="child-banner spread"
              onSubmit={async (e) => {
                e.preventDefault();
                setExitErr(null);
                try {
                  await exitChildMode(pw);
                  setPw('');
                  navigate('/');
                } catch {
                  setExitErr('That password is not right.');
                }
              }}
            >
              <span>
                <strong>Child mode</strong> for <span className="yo">{childMode.childName}</span> — grown-up settings are locked.
              </span>
              <span className="row">
                <label htmlFor="exit-pw" className="small">
                  Parent password to exit
                </label>
                <PasswordInput id="exit-pw" value={pw} onChange={setPw} autoComplete="current-password" style={{ minHeight: 34, padding: '4px 8px' }} />
                <button className="btn small secondary" type="submit">
                  Exit
                </button>
                {exitErr && <span role="alert" className="small">{exitErr}</span>}
              </span>
            </form>
          )}
          {children ?? <Outlet />}
          <UpdateBanner />
          <InstallPrompt />
        </main>
      </div>
    </div>
  );
}
